import { type VifRulesFamilyViewProps, VifRulesShell } from './VifRulesShell';
import { type BlockFlag, blockVariantConfig } from './core/blockConfig';
import type { RulesPanelCopy } from './core/rulesPanelCopy';
import type { GridConfig } from './core/types';
import { useBlockGame } from './hooks/useBlockGame';
import { useVifRulesModule } from './hooks/useVifRulesModule';

export interface VifBlockViewProps extends VifRulesFamilyViewProps {
  flag: BlockFlag;
}

/** The block board is a fixed 4x4 grid (`blockBoardEngine.ts:19-25`). */
const BLOCK_GRID: GridConfig = { rows: 4, cols: 4 };

/**
 * The canvas op button (`opBtn1`) is a four-frame clip switched by gameFlag
 * (`onSetupGame` in `games/VIFAddSub.ts` and `games/VIFMulDiv.ts`).
 */
const OPERATION: Record<BlockFlag, RulesPanelCopy['operation']> = {
  1: 'add',
  2: 'subtract',
  3: 'multiply',
  4: 'divide',
};

/**
 * DOM implementation of the 4x4 VIF block board.
 *
 * Replaces the CreateJS `VIFAddSub` and `VIFMulDiv` render paths for moduleId
 * 166-169 variant 0. Renders through `VifRulesShell` - see `VifAddSubRulesView` for
 * the shared design notes (why round/game are server-owned rather than counted
 * locally). The block stage has no rule text, so the panel shows only the
 * operation badge. Game rules, problem selection and pacing are ports of the
 * shipping engine - see `useBlockGame.ts` and `core/blockConfig.ts`.
 */
export function VifBlockView({ hubUrl, ...options }: VifBlockViewProps) {
  const block = useVifRulesModule({
    ...options,
    grid: BLOCK_GRID,
    resolveConfig: blockVariantConfig,
    useGame: useBlockGame,
  });

  return (
    <VifRulesShell
      rules={block}
      copy={{ operation: OPERATION[options.flag] }}
      hubUrl={hubUrl}
    />
  );
}
